<script lang="ts" setup>
import { useElementSize } from '@vueuse/core'

import { useListenCustomerChannel } from '@app/realtime/listen-customer-channel.hook'
import { useListenDisciplineChannel } from '@app/realtime/listen-discipline-channel.hook'
import { useUpsellBannerLoad } from '@app/layout-parts/upsell-banner/upsell-banner-load.hook'
import MobileFooter from '@app/layout-parts/header/MobileFooter.vue'
import AppHeader from '@app/layout-parts/header/AppHeader.vue'
import ModalGroupRetryPayment from '@app/layout-parts/retry-payment/ModalGroupRetryPayment.vue'
import BannerGroupRetryPayment from '@app/layout-parts/retry-payment/BannerGroupRetryPayment.vue'
import BannerUpsell from '@app/layout-parts/upsell-banner/BannerUpsell.vue'
import { useScreenWidth } from '@shared/lib/browser'
import { useSettings } from '@shared/model'
import { GateScope } from '@shared/ui/scope-gate'
import { useStoreSubscriptionActions } from '@entities/subscription'
import { useSubscriptionFailedApi } from '@entities/subscription'
import { useSubscriptionList } from '@entities/subscription'
import { useSubscriptionStore } from '@entities/subscription'
import { useLists } from '@shared/model'
import { useCustomerStore } from '@entities/customer'
import { useEcho } from '@shared/api'
import { useCommonStore } from '@shared/model'
import { LoaderFullScreenDomain } from '@shared/ui/loaders'

defineOptions({
  name: 'LayoutDefault',
})

const customerStore = useCustomerStore()
const subscriptionStore = useSubscriptionStore()
const commonStore = useCommonStore()

onBeforeMount(() => {
  useSettings()
  useLists()
  useSubscriptionList()
  useStoreSubscriptionActions(() => useSubscriptionFailedApi().get())
})

useUpsellBannerLoad()

const headerSticky = useTemplateRef('headerSticky')
const { height: headerHeight } = useElementSize(headerSticky)

const mobileFooterSticky = useTemplateRef('mobileFooterSticky')
const { height: mobileFooterHeight } = useElementSize(mobileFooterSticky)

const footer = useTemplateRef('footer')
const { height: footerHeight } = useElementSize(footer)

// Page summaries leave the mobile footer for the footer slot at 820 (media_min_w_ipad_air).
const FOOTER_TABLET_MIN_WIDTH = 820

const { isMobile } = useScreenWidth({ tabletMinWidth: FOOTER_TABLET_MIN_WIDTH })

/** ws */
const $echo = useEcho()
useListenCustomerChannel($echo)
useListenDisciplineChannel($echo)
</script>

<template>
  <div
    v-if="customerStore.hasData"
    class="cs-app"
  >
    <div
      :style="{ height: `${headerHeight}px` }"
      class="cs-app-header"
    >
      <div
        ref="headerSticky"
        class="cs-header-sticky"
      >
        <BannerGroupRetryPayment
          v-if="subscriptionStore.isBannerGroupPaymentRetry"
        />
        <template v-else>
          <BannerUpsell />
          <div
            v-if="$slots.banner"
            class="cs-header-sticky__banner"
          >
            <slot name="banner" />
          </div>
        </template>
        <AppHeader />
      </div>
    </div>
    <div class="cs-app__container">
      <div
        :style="{
          '--header-and-footer-height': `${headerHeight + footerHeight + mobileFooterHeight}px`,
          '--header-and-mobile-footer-height': `${headerHeight + mobileFooterHeight}px`,
          '--mobile-footer-height': `${mobileFooterHeight}px`,
        }"
        :class="['cs-app__main', { landing: !!$slots.landing, 'no-scroll': commonStore.isNoScroll, 'footer-overlay': customerStore.isExp90 }]"
      >
        <div
          v-if="$slots.utilityButtons"
          :style="{ '--offset-top': `${headerHeight + 24}px` }"
          class="cs-app__utility-buttons"
        >
          <slot name="utilityButtons" />
        </div>
        <slot
          v-if="$slots.landing"
          name="landing"
        />
        <GateScope v-else>
          <slot />
        </GateScope>
      </div>
      <div
        v-if="$slots.footer"
        ref="footer"
        class="cs-app-footer"
      >
        <slot name="footer" />
      </div>
    </div>
    <div :class="['cs-app-mobile-footer', { overlay: customerStore.isExp90 }]">
      <div
        v-if="isMobile"
        ref="mobileFooterSticky"
        :class="['cs-footer-sticky', { 'low-index': commonStore.isModalShowed }]"
      >
        <MobileFooter>
          <template
            v-if="$slots.mobileFooter"
            #default
          >
            <slot name="mobileFooter" />
          </template>
        </MobileFooter>
      </div>
    </div>

    <!-- FIXME: Create COMMON modals, move ModalGroupRetryPayment to COMMON modals  -->
    <ModalGroupRetryPayment />
  </div>
  <div
    v-else
    class="cs-app loader"
  >
    <LoaderFullScreenDomain />
  </div>
</template>

<style scoped lang="scss" src="@app/layouts/default.scss" />
